/**
 * planetSections.ts — central configuration for the Personal Planet
 * Observatory. All scene data, landmark specs, satellite / note
 * distributions, and contact information live here. No React, no R3F —
 * just plain TypeScript + a few Three.js types (CatmullRomCurve3 /
 * Vector3) so the file can be consumed by both the React HUD and the
 * R3F scene graph without an extra bridge.
 *
 * This file is the single source of truth for: what exists in 3D space,
 * where it is, and what it means. The visual implementations
 * (Planet, SceneLandmarks, Satellites, etc.) read this file and turn
 * these numbers into meshes.
 */

import { CatmullRomCurve3, Vector3 } from 'three';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** 3-tuple shorthand for positions / lookAts. */
export type Vec3 = [number, number, number];

/** HUD chip ids — names match what <SceneHUD /> renders. */
export type HUDId =
  | 'intro_kicker'
  | 'scroll_cue'
  | 'chapter_label'
  | 'content_card'
  | 'tag_cloud_card'
  | 'satellite_info_card'
  | 'fragment_list_card'
  | 'milestone_card'
  | 'contact_card';

/** The 6 landmark kinds that anchor each scene. */
export type LandmarkType =
  | 'house'
  | 'workshop'
  | 'skill_stones'
  | 'book_fragments'
  | 'path'
  | 'signal_tower';

/** One scene = one chapter of the scroll narrative. */
export interface SceneConfig {
  id: number;
  name: string;
  start: number; // 0..1
  end: number; // 0..1
  key: string; // panel key (also URL hash)
  title: string;
  subtitle: string;
  landmarkType: LandmarkType | 'none';
  landmarkPos: Vec3;
  /** Focal point of this chapter's diorama world (world space). Used by
   *  the tracking light. Falls back to landmarkPos when omitted. */
  focusPos?: Vec3;
  cameraPos: Vec3;
  cameraLookAt: Vec3;
  /** Screen-up direction. Surface shots use the local planet normal so
   *  architecture reads as upright against its own horizon. */
  cameraUp: Vec3;
  /** Resting lens for this chapter. Wide lenses feel close and physical;
   *  narrow lenses compress the planet and its orbit into one graphic plane. */
  cameraFov: number;
  /** Small settled dutch angle in radians. Scroll velocity is layered on top. */
  cameraRoll: number;
  /** Top/bottom matte depth in viewport-height units. Zero keeps the
   *  full frame; larger values create a chapter-specific widescreen crop. */
  cinemaBarVh: number;
  /** Reduced matte depth for narrow portrait screens. */
  cinemaBarMobileVh: number;
  /** Warm film-matte color. Intentionally brown-black rather than pure black. */
  cinemaMaskColor: string;
  /** Amount of a scene-local push toward the focal point (0..1). */
  cameraDolly: number;
  planetRotation: number; // radians
  panel: string; // data key for the HUD panel content
  showHUD: HUDId[];
  /** Directional light position for this scene. */
  lightPos: Vec3;
  /** Directional light color (hex). */
  lightColor: string;
  /** Directional light intensity (0..N). */
  lightIntensity: number;
  /** Hemisphere light intensity (0..N). */
  hemiIntensity: number;
  /** Atmosphere fresnel color (hex). */
  atmosphereColor: string;
  /** Atmosphere fresnel base opacity (0..1). */
  atmosphereOpacity: number;
  /** CSS filter string applied to the canvas wrapper for per-scene color grading. */
  sceneFilter: string;
  /** Color block hex color for the big color block layer behind the HUD. */
  colorBlockColor: string;
  /** Opacity of the scene's color block. Prefer 1 for solid poster fields. */
  colorBlockOpacity: number;
  /** Position hint for the color block's radial center. */
  colorBlockPosition: 'left' | 'right' | 'top' | 'bottom' | 'center';
  /** CSS color (rgba) for tinted glass on HUD chips. */
  glassTint: string;
  /** Flat scene field used behind the 3D world and typography. */
  baseColor: string;
  /** Primary foreground color chosen for the scene field. */
  inkColor: string;
  /** High-contrast signal color used by navigation and scene details. */
  signalColor: string;
  /** Multiplicative tint on the planet body (vertexColors × this). */
  planetTint: string;
  /** Soft emissive wash on the planet body. */
  planetEmissive: string;
  /** Wireframe shell color around the planet. */
  planetWire: string;
}

/** Helper: build a scene's filter string from per-channel parameters. */
function makeFilter(s: number, b: number, h: number): string {
  return `saturate(${s}) brightness(${b}) hue-rotate(${h}deg)`;
}

/** Material hint for a landmark's glowing color. */
export type GlowColor = 'mint' | 'gold' | 'warm' | 'cool' | 'none';

/** Generic dimensions — interpreted differently by each landmark. */
export interface Dimensions {
  /** Approx "footprint" radius in world units. */
  footprint: number;
  /** Vertical extent (cylinder/box height, ring radius, etc.). */
  height: number;
  /** Inner / minor / secondary axis when relevant. */
  minor?: number;
}

/** Per-landmark visuals and metadata. */
export interface LandmarkSpec {
  type: LandmarkType;
  label: string;
  /** Short hand-set blurb — 4-7 words — for the HUD card. */
  caption: string;
  dimensions: Dimensions;
  materials: {
    primary: string; // hex
    secondary?: string; // hex
    emissive?: string; // hex
    emissiveIntensity?: number;
    opacity?: number;
  };
  glow: GlowColor;
  /** 'always' = visible throughout; 'scene' = only when its scene is active. */
  visibility: 'always' | 'scene';
  /** Does the landmark have an interactive group? */
  interactive: boolean;
}

// ---------------------------------------------------------------------------
// SCENES — 7 chapters, 700vh of scroll.
// Source: design brief. start/end are normalized 0..1 scroll progress.
// Each scene's landmark sits at `landmarkPos` (rotated by the planet's
// group rotation) and is the focal point the camera looks at.
// ---------------------------------------------------------------------------

export const SCENES: SceneConfig[] = [
  {
    id: 0,
    name: 'Wake',
    start: 0,
    end: 0.15,
    key: 'intro',
    title: 'yhazrin',
    subtitle: 'small tools, careful writing, and a slow build',
    landmarkType: 'none',
    landmarkPos: [0, 0, 0],
    cameraPos: [0, 0.2, 8.8],
    cameraLookAt: [-0.55, 0.05, 0],
    cameraUp: [0, 1, 0],
    cameraFov: 36,
    cameraRoll: 0,
    cinemaBarVh: 0,
    cinemaBarMobileVh: 0,
    cinemaMaskColor: '#24150f',
    cameraDolly: 0.04,
    planetRotation: 0,
    panel: 'intro',
    showHUD: ['intro_kicker', 'scroll_cue'],
    lightPos: [5, 5, 5],
    lightColor: '#ffe7c7',
    lightIntensity: 1.12,
    hemiIntensity: 0.5,
    atmosphereColor: '#79e6c2',
    atmosphereOpacity: 0.18,
    sceneFilter: makeFilter(1.12, 1.04, -4),
    colorBlockColor: '#ff5a3d',
    colorBlockOpacity: 0.86,
    colorBlockPosition: 'center',
    glassTint: 'rgba(121,230,194,0.12)',
    baseColor: '#080d18',
    inkColor: '#f5f0e8',
    signalColor: '#ff5a3d',
    planetTint: '#7fc2ad',
    planetEmissive: '#ff6b4a',
    planetWire: '#b7f5de',
  },
  {
    id: 1,
    name: 'About',
    start: 0.15,
    end: 0.3,
    key: 'about',
    title: 'About',
    subtitle: 'A small house beside a city marker',
    landmarkType: 'house',
    landmarkPos: [2.18, 0.52, 1.02],
    // Ground-skimming wide shot: the planet becomes a horizon and the
    // Paris marker rises from it instead of reading as a miniature.
    cameraPos: [1.12, 0.36, 1.57],
    cameraLookAt: [1.68, 0.4, 0.79],
    cameraUp: [0.885, 0.211, 0.414],
    cameraFov: 58,
    cameraRoll: -0.018,
    cinemaBarVh: 4,
    cinemaBarMobileVh: 2.5,
    cinemaMaskColor: '#351a13',
    cameraDolly: 0.1,
    planetRotation: 1.2566370614359172, // ~72°
    panel: 'about',
    showHUD: ['chapter_label', 'content_card'],
    lightPos: [5, 2, 5],
    lightColor: '#fff0c8',
    lightIntensity: 0.9,
    hemiIntensity: 0.45,
    atmosphereColor: '#ffe44d',
    atmosphereOpacity: 0.15,
    sceneFilter: makeFilter(1.18, 1.02, -10),
    colorBlockColor: '#ffe44d',
    colorBlockOpacity: 0.88,
    colorBlockPosition: 'left',
    glassTint: 'rgba(255,228,77,0.16)',
    baseColor: '#d95444',
    inkColor: '#fff4e8',
    signalColor: '#ffe44d',
    planetTint: '#dc795e',
    planetEmissive: '#ffe44d',
    planetWire: '#fff4e8',
  },
  {
    id: 2,
    name: 'Skills',
    start: 0.3,
    end: 0.45,
    key: 'skills',
    title: 'Skills',
    subtitle: 'Standing stones and a quiet mountain gate',
    landmarkType: 'skill_stones',
    landmarkPos: [-1.42, 1.36, 2.08],
    // Local top-down view: the ridge reads like a map cut into the globe.
    cameraPos: [-2.48, 2.38, 3.64],
    cameraLookAt: [-0.82, 0.79, 1.2],
    cameraUp: [0, 1, 0],
    cameraFov: 31,
    cameraRoll: 0.028,
    cinemaBarVh: 9.5,
    cinemaBarMobileVh: 5,
    cinemaMaskColor: '#332219',
    cameraDolly: 0.08,
    planetRotation: 2.199114857512855, // ~126°
    panel: 'skills',
    showHUD: ['chapter_label', 'tag_cloud_card'],
    lightPos: [5, 0.5, 5],
    lightColor: '#e9f1d4',
    lightIntensity: 0.58,
    hemiIntensity: 0.4,
    atmosphereColor: '#173ac2',
    atmosphereOpacity: 0.13,
    sceneFilter: makeFilter(1.2, 1.03, 14),
    colorBlockColor: '#1239ff',
    colorBlockOpacity: 0.84,
    colorBlockPosition: 'left',
    glassTint: 'rgba(18,57,255,0.16)',
    baseColor: '#a9c971',
    inkColor: '#10140b',
    signalColor: '#1239ff',
    planetTint: '#93b887',
    planetEmissive: '#1239ff',
    planetWire: '#e9f1d4',
  },
  {
    id: 3,
    name: 'Projects',
    start: 0.45,
    end: 0.65,
    key: 'projects',
    title: "Things I've shipped",
    subtitle: 'Satellites above a small skyline',
    landmarkType: 'workshop',
    landmarkPos: [0.9, -0.9, 2.48],
    // Long-lens orbital tableau: tiny satellites and the full planet share
    // a compressed plane, creating an explicit large/small scale contrast.
    cameraPos: [5.6, -3.8, 8.6],
    cameraLookAt: [0.56, -0.56, 1.56],
    cameraUp: [0, 1, 0],
    cameraFov: 24,
    cameraRoll: -0.012,
    cinemaBarVh: 13.5,
    cinemaBarMobileVh: 6.5,
    cinemaMaskColor: '#261712',
    cameraDolly: 0.05,
    planetRotation: 3.141592653589793, // 180°
    panel: 'projects',
    showHUD: ['chapter_label', 'satellite_info_card'],
    lightPos: [5, -1, 5],
    lightColor: '#F5EFEA',
    lightIntensity: 0.45,
    hemiIntensity: 0.38,
    atmosphereColor: '#8ec5ff',
    atmosphereOpacity: 0.15,
    sceneFilter: makeFilter(1.08, 1.02, -6),
    colorBlockColor: '#ff6b4a',
    colorBlockOpacity: 0.78,
    colorBlockPosition: 'right',
    glassTint: 'rgba(142,197,255,0.14)',
    // Deep sea blue field + soft cream type (THINGS I SENT OUT)
    baseColor: '#173574',
    inkColor: '#F5EFEA',
    signalColor: '#ff6b4a',
    planetTint: '#365b9f',
    planetEmissive: '#8ec5ff',
    planetWire: '#F5EFEA',
  },
  {
    id: 4,
    name: 'Notes',
    start: 0.65,
    end: 0.8,
    key: 'notes',
    title: 'Recent notes',
    subtitle: 'Fragments drifting above a harbor',
    landmarkType: 'book_fragments',
    landmarkPos: [-1.86, 1.9, -0.92],
    // Close horizon view with fragments crossing the near field.
    cameraPos: [-0.77, 1.28, -1.61],
    cameraLookAt: [-1.22, 1.25, -0.61],
    cameraUp: [-0.661, 0.675, -0.327],
    cameraFov: 52,
    cameraRoll: 0.02,
    cinemaBarVh: 6,
    cinemaBarMobileVh: 3.5,
    cinemaMaskColor: '#382018',
    cameraDolly: 0.08,
    planetRotation: 4.71238898038469, // 270°
    panel: 'notes',
    showHUD: ['chapter_label', 'fragment_list_card'],
    lightPos: [5, 0.5, 5],
    lightColor: '#01847F',
    lightIntensity: 0.55,
    hemiIntensity: 0.42,
    atmosphereColor: '#01847F',
    atmosphereOpacity: 0.14,
    sceneFilter: makeFilter(1.1, 1.03, 4),
    colorBlockColor: '#01847F',
    colorBlockOpacity: 0.82,
    colorBlockPosition: 'top',
    glassTint: 'rgba(1,132,127,0.14)',
    // Soft pink paper + teal ink (notes / fragments)
    baseColor: '#e4cad4',
    inkColor: '#0b5f5b',
    signalColor: '#0b786f',
    planetTint: '#c08fa4',
    planetEmissive: '#0b786f',
    planetWire: '#0b786f',
  },
  {
    id: 5,
    name: 'Timeline',
    start: 0.8,
    end: 0.92,
    key: 'timeline',
    title: 'Timeline',
    subtitle: 'A glowing path around the planet',
    landmarkType: 'path',
    landmarkPos: [0, 0, 0],
    // Near-vertical orbital plan view so the path can be read in one glance.
    cameraPos: [0.12, 7.8, 0.8],
    cameraLookAt: [0, 0, 0],
    cameraUp: [0, 0, -1],
    cameraFov: 32,
    cameraRoll: -0.035,
    cinemaBarVh: 15,
    cinemaBarMobileVh: 7,
    cinemaMaskColor: '#2d1a12',
    cameraDolly: 0.03,
    planetRotation: 5.969026041820607, // ~342°
    panel: 'timeline',
    showHUD: ['chapter_label', 'milestone_card'],
    lightPos: [5, 3, 5],
    lightColor: '#f5f0e8',
    lightIntensity: 0.7,
    hemiIntensity: 0.55,
    atmosphereColor: '#ff7a59',
    atmosphereOpacity: 0.16,
    sceneFilter: makeFilter(1.18, 1.04, 8),
    colorBlockColor: '#ff7a59',
    colorBlockOpacity: 0.82,
    colorBlockPosition: 'left',
    glassTint: 'rgba(255,45,149,0.18)',
    baseColor: '#087c70',
    inkColor: '#f5f0e8',
    signalColor: '#ff7a59',
    planetTint: '#3da58d',
    planetEmissive: '#ff7a59',
    planetWire: '#f5f0e8',
  },
  {
    id: 6,
    name: 'Contact',
    start: 0.92,
    end: 1,
    key: 'contact',
    title: 'Contact',
    subtitle: 'Signal tower on the planet',
    landmarkType: 'signal_tower',
    landmarkPos: [-1, -0.5, -3],
    // Low horizon finale looking upward through the signal tower.
    cameraPos: [0.59, -0.27, -2.02],
    cameraLookAt: [-0.62, -0.31, -1.87],
    cameraUp: [-0.312, -0.156, -0.937],
    cameraFov: 49,
    cameraRoll: 0.035,
    cinemaBarVh: 10.5,
    cinemaBarMobileVh: 5.5,
    cinemaMaskColor: '#36180f',
    cameraDolly: 0.08,
    planetRotation: 7.225663103256523, // ~414°
    panel: 'contact',
    showHUD: ['chapter_label', 'contact_card'],
    lightPos: [5, 2, 5],
    lightColor: '#fff0c8',
    lightIntensity: 0.72,
    hemiIntensity: 0.4,
    atmosphereColor: '#d73d2f',
    atmosphereOpacity: 0.14,
    sceneFilter: makeFilter(1.15, 1.05, -2),
    colorBlockColor: '#d73d2f',
    colorBlockOpacity: 0.84,
    colorBlockPosition: 'right',
    glassTint: 'rgba(255,59,48,0.18)',
    baseColor: '#d9b549',
    inkColor: '#171100',
    signalColor: '#d73d2f',
    planetTint: '#cda842',
    planetEmissive: '#d73d2f',
    planetWire: '#fff0c8',
  },
];

/** Convenience: scene lookup by key. */
export const SCENES_BY_KEY: Record<string, SceneConfig> = Object.fromEntries(
  SCENES.map((s) => [s.key, s]),
);

// ---------------------------------------------------------------------------
// LANDMARKS — 6 architectural kinds. Each scene points at one. The scene
// graph will read this registry to know what to instantiate.
// ---------------------------------------------------------------------------

export const LANDMARKS: Record<LandmarkType, LandmarkSpec> = {
  house: {
    type: 'house',
    label: 'House',
    caption: 'Where she lives and writes',
    dimensions: { footprint: 0.55, height: 0.7, minor: 0.4 },
    materials: {
      primary: '#7fb89f', // mint body
      secondary: '#f5f0e8', // warm trim
      emissive: '#7fb89f',
      emissiveIntensity: 0.15,
    },
    glow: 'warm',
    visibility: 'scene',
    interactive: true,
  },
  workshop: {
    type: 'workshop',
    label: 'Workshop',
    caption: 'Where things get shipped',
    dimensions: { footprint: 0.9, height: 0.45, minor: 0.6 },
    materials: {
      primary: '#F5EFEA', // cream body on deep-sea field
      secondary: '#122E8A',
      emissive: '#F5EFEA',
      emissiveIntensity: 0.16,
    },
    glow: 'cool',
    visibility: 'scene',
    interactive: true,
  },
  skill_stones: {
    type: 'skill_stones',
    label: 'Skill stones',
    caption: 'Six tags, six stones',
    dimensions: { footprint: 1.2, height: 1.4, minor: 0.3 },
    materials: {
      primary: '#5a5a5a', // muted stone
      secondary: '#7fb89f', // mint tip
      emissive: '#7fb89f',
      emissiveIntensity: 0.1,
      opacity: 0.9,
    },
    glow: 'cool',
    visibility: 'scene',
    interactive: true,
  },
  book_fragments: {
    type: 'book_fragments',
    label: 'Book fragments',
    caption: 'Five recent notes in the air',
    dimensions: { footprint: 1.4, height: 1.0, minor: 0.18 },
    materials: {
      primary: '#F9D2E4', // pink paper
      secondary: '#01847F', // teal spine
      emissive: '#01847F',
      emissiveIntensity: 0.1,
      opacity: 0.88,
    },
    glow: 'cool',
    visibility: 'scene',
    interactive: true,
  },
  path: {
    type: 'path',
    label: 'Timeline path',
    caption: 'Six milestones since 2019',
    dimensions: { footprint: 2.6, height: 0.05, minor: 0.08 },
    materials: {
      primary: '#7fb89f', // glowing path
      secondary: '#b8a572', // milestone markers
      emissive: '#7fb89f',
      emissiveIntensity: 0.4,
      opacity: 0.75,
    },
    glow: 'mint',
    visibility: 'scene',
    interactive: true,
  },
  signal_tower: {
    type: 'signal_tower',
    label: 'Signal tower',
    caption: 'Reach out, she replies',
    dimensions: { footprint: 0.18, height: 1.6, minor: 0.1 },
    materials: {
      primary: '#b8a572', // gold tower
      secondary: '#7fb89f', // mint antenna
      emissive: '#b8a572',
      emissiveIntensity: 0.25,
    },
    glow: 'gold',
    visibility: 'scene',
    interactive: true,
  },
};

/** Convenience list of all landmark types, in scene order. */
export const LANDMARK_TYPES: LandmarkType[] = SCENES.map(
  (s) => s.landmarkType,
).filter((t): t is LandmarkType => t !== 'none');

// ---------------------------------------------------------------------------
// SATELLITES — one per project, in a stable orbit around the planet.
// Orbit radius / speed / phase are derived from each project's color
// hash so the same project always lands in the same orbit.
// ---------------------------------------------------------------------------

export interface ProjectLink {
  label: string;
  url: string;
}

export interface ArchitectureNode {
  id: string;
  label: string;
}

export interface ArchitectureSpec {
  nodes: ArchitectureNode[];
  edges: { from: string; to: string }[];
}

export interface GalleryItem {
  label: string;
}

export interface SatelliteConfig {
  id: string;
  name: string;
  blurb: string;
  /** Long-form blurb (2-3 sentences) for the deep-dive panel. */
  longBlurb?: string;
  /** The author's role on this project (e.g. "creator", "maintainer"). */
  role?: string;
  /** 3-4 short bullets: what was novel, what was hard, what shipped. */
  highlights?: string[];
  tech: string[];
  year: string;
  color: string;
  /** World units from planet center. */
  orbitRadius: number;
  /** Radians per second. */
  orbitSpeed: number;
  /** Initial phase on the orbit, radians. */
  orbitPhase: number;
  /** Orbital tilt (inclination) in radians, gives variety. */
  orbitTilt: number;
  /** World scale of the satellite mesh. */
  scale: number;
  /** Does the satellite have an interactive group? */
  interactive: boolean;
  /** Optional architecture diagram for the detail panel. */
  architecture?: ArchitectureSpec;
  /** Optional out-going links. */
  links?: ProjectLink[];
  /** Optional placeholder gallery tiles. */
  gallery?: GalleryItem[];
}

/** Projects content (mirror of Work section). */
export const SATELLITES: SatelliteConfig[] = [
  {
    id: 'vicoo',
    name: 'VICOO',
    blurb:
      'Knowledge workspace built around notes, timeline, and a galaxy view of connected ideas. React + TipTap + a custom sync layer.',
    longBlurb:
      'VICOO is the project I have spent the most time on in the last two years. It started as a private notes app, then grew a timeline, then a galaxy view, then a tiny sync layer. The whole thing is a single React + TypeScript SPA with a Node.js persistence layer and a Postgres backend; the editor is TipTap on top of ProseMirror. The public beta shipped in 2024 with about 200 weekly actives and has stayed roughly flat since — I am not chasing growth, just keeping it alive for the people who use it daily.',
    role: 'creator · sole maintainer',
    highlights: [
      'Built a CRDT-based sync layer in ~600 lines of TypeScript, with offline editing and last-writer-wins fallbacks for conflicts the CRDT does not cover.',
      'Migrated the editor from Slate to TipTap mid-2023 — the ProseMirror transaction model ended up being a much better fit for the timeline view than Slate ops were.',
      'Shipped the public beta in 2024 with no marketing push; the hardest part was the migration tooling that moved early users from the v1 file-based storage to Postgres without losing formatting.',
      'Stays online on a single 4GB VPS. Postgres, a Node process, and a Caddy reverse proxy is the entire stack.',
    ],
    tech: ['React', 'TipTap', 'Node.js', 'Postgres'],
    year: '2024',
    color: '#F5EFEA',
    orbitRadius: 2.4,
    orbitSpeed: 0.08,
    orbitPhase: 0,
    orbitTilt: 0.15,
    scale: 0.18,
    interactive: true,
    architecture: {
      nodes: [
        { id: 'ui', label: 'UI · React + TipTap' },
        { id: 'sync', label: 'Sync · CRDT + WebSocket' },
        { id: 'api', label: 'API · Node + Fastify' },
        { id: 'db', label: 'DB · Postgres + S3' },
      ],
      edges: [
        { from: 'ui', to: 'sync' },
        { from: 'sync', to: 'api' },
        { from: 'api', to: 'db' },
      ],
    },
    links: [
      { label: 'Website', url: 'https://vicoo.app' },
      { label: 'Source', url: 'https://github.com/Yhazrin/vicoo' },
      { label: 'Changelog', url: 'https://vicoo.app/changelog' },
    ],
    gallery: [
      { label: 'Editor' },
      { label: 'Timeline' },
      { label: 'Galaxy' },
      { label: 'Settings' },
    ],
  },
  {
    id: 'awesome-design-md',
    name: 'awesome-design-md',
    blurb:
      'A curated markdown design reference collection — annotated, opinionated, single-author.',
    longBlurb:
      'awesome-design-md is a personal reading list that grew into a public reference. The repo is just a folder of markdown files, each one a short essay or excerpt about a piece of design work — a website, a poster, a typeface, a UI pattern — with a paragraph or two on what is worth taking away. The whole thing is single-author, no build step, no CMS; you clone the repo and read it like a book.',
    role: 'creator · curator',
    highlights: [
      'About 60 entries so far, each ~400 words, each annotated with what to look at and why it matters.',
      'No build step — just markdown files committed straight to main. The preview is GitHub Pages with a single CSS file for typography and a small index that links them all.',
      'Updated roughly twice a month, whenever I run into something I want to remember. The commit log is the changelog; the diffs read like a reading diary.',
    ],
    tech: ['Markdown', 'GitHub Pages'],
    year: '2025',
    color: '#9eb0e8',
    orbitRadius: 2.7,
    orbitSpeed: 0.075,
    orbitPhase: 1.0471975511965976, // π/3
    orbitTilt: -0.1,
    scale: 0.13,
    interactive: true,
    architecture: {
      nodes: [
        { id: 'md', label: 'Content · Markdown files' },
        { id: 'css', label: 'Theme · single CSS file' },
        { id: 'pages', label: 'Serve · GitHub Pages' },
        { id: 'dns', label: 'DNS · custom domain' },
      ],
      edges: [
        { from: 'md', to: 'css' },
        { from: 'css', to: 'pages' },
        { from: 'pages', to: 'dns' },
      ],
    },
    links: [
      { label: 'GitHub', url: 'https://github.com/Yhazrin/awesome-design-md' },
      { label: 'Preview', url: 'https://awesome-design.yhazrin.xyz' },
    ],
    gallery: [
      { label: 'Index' },
      { label: 'Article' },
      { label: 'Annotations' },
      { label: 'About' },
    ],
  },
  {
    id: 'tldraw-replay',
    name: 'tldraw-replay',
    blurb: 'Deterministic replay engine for tldraw sessions.',
    longBlurb:
      'A small library that records a tldraw session into a compact event log and replays it deterministically. Useful for tutorials, bug reports, and embedding sessions in docs. The replay engine is pure TypeScript with no external services — you drop it in, call record(), and ship a serialized session as JSON.',
    role: 'creator · open source maintainer',
    highlights: [
      'Headless tldraw is the trick that makes this work — you can mount the editor on a JSDOM canvas and feed it a recorded event log without any user interaction.',
      'The hardest part was normalising pointer events across input types. A trackpad pan and a touch drag look like the same event in tldraw but not in the browser; the library reconciles them into a single canonical log.',
      'Shipped to npm in early 2025. Currently around 1.2k weekly downloads, mostly from docs sites and tutorial embeds.',
      'A side benefit: the same log format powers the "rewind" button in my own note-taking app, so VICOO embeds replays of how a whiteboard section was drawn.',
    ],
    tech: ['tldraw', 'TypeScript'],
    year: '2025',
    color: '#F5EFEA',
    orbitRadius: 3.0,
    orbitSpeed: 0.06,
    orbitPhase: 2.0943951023931953, // 2π/3
    orbitTilt: -0.12,
    scale: 0.16,
    interactive: true,
    architecture: {
      nodes: [
        { id: 'rec', label: 'Recorder · tldraw events' },
        { id: 'log', label: 'Log · JSON event log' },
        { id: 'replay', label: 'Replay · headless tldraw' },
      ],
      edges: [
        { from: 'rec', to: 'log' },
        { from: 'log', to: 'replay' },
      ],
    },
    links: [
      { label: 'npm', url: 'https://www.npmjs.com/package/tldraw-replay' },
      { label: 'Source', url: 'https://github.com/Yhazrin/tldraw-replay' },
    ],
    gallery: [
      { label: 'Recorder' },
      { label: 'Player' },
    ],
  },
  {
    id: 'cli-todo',
    name: 'cli-todo',
    blurb: '4KB terminal task manager written in Zig.',
    longBlurb:
      'A tiny terminal task manager written in Zig. The whole binary is 4KB stripped, with zero runtime dependencies. It stores everything in a single SQLite file in your home directory, and the interface is just a few keyboard shortcuts. I wrote it because I wanted a todo list that started instantly, did not phone home, and would still work in 20 years.',
    role: 'creator',
    highlights: [
      'The whole binary is 4KB stripped. The trick is a hand-written SQLite vtable in Zig and a 600-line TUI that bypasses ncurses in favour of raw ANSI escape codes.',
      'No config file. No plugin system. The CLI surface is six subcommands and the TUI is one screen — adding a feature is a fight with yourself.',
      'Cross-compiles from a single Linux host to macOS, Linux, and Windows in about 8 seconds. CI builds all three on every push.',
      'I use it daily. It is the only project on this list that has not changed in six months, which is exactly the point.',
    ],
    tech: ['Zig', 'SQLite'],
    year: '2025',
    color: '#01847F',
    orbitRadius: 3.6,
    orbitSpeed: 0.05,
    orbitPhase: 3.141592653589793, // π
    orbitTilt: 0.18,
    scale: 0.14,
    interactive: true,
    architecture: {
      nodes: [
        { id: 'cli', label: 'CLI · Zig parser' },
        { id: 'store', label: 'Store · SQLite' },
        { id: 'tui', label: 'TUI · raw ANSI' },
      ],
      edges: [
        { from: 'cli', to: 'store' },
        { from: 'cli', to: 'tui' },
      ],
    },
    links: [
      { label: 'Binaries', url: 'https://github.com/Yhazrin/cli-todo/releases' },
      { label: 'Source', url: 'https://github.com/Yhazrin/cli-todo' },
    ],
    gallery: [
      { label: 'List' },
      { label: 'Add' },
    ],
  },
  {
    id: 'blog',
    name: 'blog.yhazrin.xyz',
    blurb:
      'This site. Static, served from a single VPS, built with the geomotion design system.',
    longBlurb:
      'This site. The front-end is a single Vite + React build; the content layer is plain Markdown with frontmatter; the deployment is a single VPS running OpenResty. No database, no CDN, no third-party scripts. The whole thing loads in under 200ms on a cold cache. The 3D planet you are looking at is built with @react-three/fiber and procedural geometry only — no GLB imports.',
    role: 'this site',
    highlights: [
      'The planet you are reading on is a single R3F scene graph with ~40 nodes — planet body, atmosphere, six landmark meshes, four satellite meshes, five note fragments, and a timeline spline. No external 3D models.',
      'Every chapter is a scene with its own lighting and color block, driven by a single SCENES config in planetSections.ts. Adding a new chapter is a one-file change.',
      'Total build is ~310KB gzipped including React, Three.js, and motion. The planet is the heaviest single dependency and there is no way to drop it without losing the whole point.',
      'Deployed via a single rsync to a 4GB VPS running OpenResty. Total deploy time, including the rsync, is under 4 seconds.',
    ],
    tech: ['Vite', 'React', 'motion', 'OpenResty'],
    year: '2026',
    color: '#F9D2E4',
    orbitRadius: 4.2,
    orbitSpeed: 0.04,
    orbitPhase: 4.1887902047863905, // 4π/3
    orbitTilt: -0.08,
    scale: 0.15,
    interactive: true,
    architecture: {
      nodes: [
        { id: 'pages', label: 'Pages · React + Vite' },
        { id: 'md', label: 'Content · Markdown + frontmatter' },
        { id: 'build', label: 'Build · static export' },
        { id: 'serve', label: 'Serve · OpenResty + VPS' },
      ],
      edges: [
        { from: 'md', to: 'pages' },
        { from: 'pages', to: 'build' },
        { from: 'build', to: 'serve' },
      ],
    },
    links: [
      { label: 'Home', url: '/' },
      { label: 'Feed', url: '/feed.xml' },
      { label: 'Source', url: 'https://github.com/Yhazrin/blog-yhazrin' },
    ],
    gallery: [
      { label: 'Hero' },
      { label: 'Posts' },
      { label: 'Planet' },
    ],
  },
  {
    id: 'yhazrin-panel',
    name: 'yhazrin-panel',
    blurb:
      'A small Next.js + shadcn ops panel for this site and the planet.',
    longBlurb:
      'yhazrin-panel is the operations dashboard for blog.yhazrin.xyz. It is a single Next.js App Router app using shadcn/ui primitives, with three views: VPS health scrape, the last few deploys, and the webhook log. The Mac mini at home POSTs back to it when a long-running scrape job finishes, and every inbound request is HMAC-signed so the panel does not have to trust the network. The whole thing is about 30 components in a single src/components/ui directory.',
    role: 'creator · sole maintainer',
    highlights: [
      'Single-page Next.js App Router app with three views: OPS dashboard, VPS scrape, Mac mini webhook log.',
      'HMAC-signed requests on the webhook endpoint so the Mac mini can post back without a shared secret in plaintext — the key never leaves the VPS.',
      'Built on shadcn/ui primitives with a small custom auth layer; the bundle ships in under 200KB gz and deploys in about 8 seconds.',
    ],
    tech: ['Next.js', 'shadcn/ui', 'HMAC', 'VPS'],
    year: '2026',
    color: '#7ec8c4',
    orbitRadius: 4.8,
    orbitSpeed: 0.035,
    orbitPhase: 5.235987755982989, // 5π/3
    orbitTilt: 0.2,
    scale: 0.17,
    interactive: true,
    architecture: {
      nodes: [
        { id: 'ops', label: 'OPS · Next.js + shadcn' },
        { id: 'auth', label: 'HMAC · request signing' },
        { id: 'vps', label: 'VPS · health scrape' },
        { id: 'mac', label: 'Mac mini · webhook' },
      ],
      edges: [
        { from: 'ops', to: 'auth' },
        { from: 'ops', to: 'vps' },
        { from: 'ops', to: 'mac' },
      ],
    },
    links: [
      { label: 'Live', url: 'https://panel.yhazrin.xyz' },
      { label: 'Source', url: 'https://github.com/Yhazrin/yhazrin-panel' },
    ],
    gallery: [
      { label: 'Dashboard' },
      { label: 'Deploys' },
      { label: 'Webhooks' },
      { label: 'Settings' },
    ],
  },
];

// ---------------------------------------------------------------------------
// NOTES — floating fragments around the planet. Positions are spread on
// a sphere of radius `positionOnOrbit.sphereRadius` at random angles,
// with a small per-note jitter so they don't sit on a perfect grid.
// ---------------------------------------------------------------------------

export interface NoteConfig {
  id: number;
  title: string;
  date: string;
  read: string;
  tag: string;
  /** Short lede shown in the detail panel. */
  excerpt?: string;
  /** Long-form body paragraphs for the detail panel. */
  body?: string[];
  /** Extra tag chips shown alongside the primary tag. */
  tags?: string[];
  positionOnOrbit: { sphereRadius: number; theta: number; phi: number };
  /** Mesh scale. */
  scale: number;
  /** Per-note spin axis, normalized Vector3. */
  spinAxis: Vec3;
  /** Spin rate, radians/sec. */
  spinSpeed: number;
  interactive: boolean;
}

/** Deterministic-ish spread: notes orbit on a sphere of radius 4.2-4.8. */
export const NOTES: NoteConfig[] = [
  {
    id: 1,
    title: 'The cost of cleverness',
    date: '2026-06-21',
    read: '5 min',
    tag: 'essay',
    excerpt:
      'A short piece on why I keep choosing the boring option, and what it has cost me over the years.',
    body: [
      'I keep choosing the boring option. Boring stack, boring editor, boring defaults. The cost of cleverness is invisible until you have to pay it — usually at 2am, or six months in, when the clever thing breaks and nobody on the team can remember why it was clever in the first place.',
      'This is not an argument for bland software. Cleverness has its place: in a single function, in a one-off demo, in a tiny library that nobody else has to maintain. The problem starts when clever patterns become load-bearing — when the architecture of a system quietly depends on a trick that only one person fully understands.',
      'The cost compounds. A clever abstraction in week one becomes a clever abstraction in month twelve that needs a clever migration. The team learns to route around it. New engineers avoid the file. The clever thing becomes a kind of architectural debt, except worse, because nobody recognises it as debt — it still works, after a fashion.',
      'So I keep choosing the boring option. Not because boring is good, but because boring is amortised. The cost of boring is paid up front, in the first commit. The cost of cleverness is paid forever.',
    ],
    tags: ['philosophy', 'software'],
    positionOnOrbit: { sphereRadius: 4.4, theta: 0, phi: 1.2 },
    scale: 0.32,
    spinAxis: [0.2, 1, 0.1],
    spinSpeed: 0.05,
    interactive: true,
  },
  {
    id: 2,
    title: 'Why I stopped reaching for Redux',
    date: '2026-05-09',
    read: '7 min',
    tag: 'frontend',
    excerpt:
      'Notes on a multi-year migration away from a global store, and the local patterns that replaced it.',
    body: [
      'For most of the last decade, my default React state management answer was "add Redux". This was a fine default. It is no longer my default, and the reason is mostly about surface area.',
      'A global store is a great way to model data that genuinely is global — auth, theme, locale, feature flags. It is a poor way to model data that happens to be shared between two siblings. Every "I just need to share this with that one component" ends up as a slice, a reducer, an action type, three files of boilerplate, and a runtime that has to reconcile everything.',
      'What replaced it, in practice: React context for genuinely global data, a tiny custom store (often 30 lines of useSyncExternalStore) for the small number of features that benefit from one, and a healthy respect for prop drilling past two levels — it is almost always the right answer.',
      'Redux still has a place. But it is no longer the first thing I reach for. The first thing I reach for is the smallest thing that works, and then I add structure only when the structure is the point.',
    ],
    tags: ['react', 'state', 'patterns'],
    positionOnOrbit: { sphereRadius: 4.6, theta: 0.7853981633974483, phi: 0.4 },
    scale: 0.3,
    spinAxis: [0.1, 0.9, 0.3],
    spinSpeed: 0.04,
    interactive: true,
  },
  {
    id: 3,
    title: 'Designing a calendar you actually read',
    date: '2026-04-02',
    read: '4 min',
    tag: 'design',
    excerpt:
      'On the small interface decisions that make a calendar feel like something you open on purpose, not something that nags you.',
    body: [
      'Most calendars feel like a notification surface. They vibrate, they ping, they remind. Mine, for a long time, was a chore. I opened it when I had to, not when I wanted to. The redesign that fixed it was not about features — it was about reading.',
      'A calendar you actually read is calm. It does not shout. It uses the same type, the same spacing, the same color treatment for things you have already decided. The new things are clearly the new things, but the contrast is gentle, not aggressive.',
      'There is a small typography trick that helped a lot: set the day-of-week header in a smaller, lighter weight, and let the day numbers carry the visual weight. The header becomes a label, not a flag. Your eye lands on the date, then drifts up to find the day name, instead of being pushed there by a bold bar.',
      'I also stopped trying to be smart. No weather icons, no "focus time" suggestions, no AI summaries of my week. The calendar is a calendar. The cleverness is in the restraint.',
    ],
    tags: ['ui', 'typography'],
    positionOnOrbit: { sphereRadius: 4.2, theta: 1.5707963267948966, phi: 1.7 },
    scale: 0.28,
    spinAxis: [-0.2, 1, 0.2],
    spinSpeed: 0.06,
    interactive: true,
  },
  {
    id: 4,
    title: 'A tiny static site pipeline',
    date: '2026-02-18',
    read: '3 min',
    tag: 'build',
    excerpt:
      'How this site gets from a folder of markdown to a CDN-served directory listing, with almost no glue.',
    body: [
      'I am allergic to build pipelines that have more config than source. The pipeline that ships this site is intentionally tiny: a single Vite build, a small Node script that walks the dist directory, a single rsync to the VPS, and a one-line OpenResty config that serves the directory.',
      'The content layer is just markdown files in a /content directory. Each file has a small frontmatter block with title, date, and tags. There is no CMS, no database, no admin UI. To publish a post, I write a file and commit it.',
      'The whole build takes about 12 seconds on a clean checkout. The deploy takes about 2. There is no staging environment, no preview URL, no rollback story beyond "git revert". This sounds fragile. In practice, it is the most reliable thing I have ever shipped.',
    ],
    tags: ['deploy', 'static'],
    positionOnOrbit: { sphereRadius: 4.8, theta: 2.356194490192345, phi: 2.3 },
    scale: 0.26,
    spinAxis: [0.3, 0.8, -0.1],
    spinSpeed: 0.045,
    interactive: true,
  },
  {
    id: 5,
    title: 'Notes on local-first',
    date: '2026-01-11',
    read: '6 min',
    tag: 'thought',
    excerpt:
      'Some scattered thoughts on what "local-first" means to me, and why I think the term is in danger of becoming a marketing word.',
    body: [
      'Local-first is, in 2026, a word that means too many things. To some it means "the app works offline". To others it means "your data is yours". To a third group it means "we use CRDTs". These are not the same claim, and they have very different engineering consequences.',
      'My preferred definition is the boring one: a local-first app is one that can do useful work without talking to a server. The data lives on your device. Sync is a feature, not a requirement. The server, if there is one, is a coordinator, not a source of truth.',
      'This definition has the advantage of being testable. Open the app on a plane. Does it work? If yes, it is local-first. If it shows a spinner, it is not — it is a web app with a sync layer, which is a different and also valid thing, but it is not local-first.',
      'I worry the term is being diluted. Marketing pages use it the way they use "AI": to mean "we have done something clever with this, trust us". The only real defense is to keep the definition narrow and to call out the violations when you see them.',
    ],
    tags: ['sync', 'philosophy'],
    positionOnOrbit: { sphereRadius: 4.5, theta: 3.141592653589793, phi: 0.9 },
    scale: 0.3,
    spinAxis: [0.1, 1, -0.3],
    spinSpeed: 0.05,
    interactive: true,
  },
  {
    id: 6,
    title: 'On the small joys of slow tools',
    date: '2025-12-22',
    read: '5 min',
    tag: 'thought',
    excerpt:
      'A short note on why my favorite tools are the slow ones, and what they keep teaching me.',
    body: [
      'My favorite tools are the slow ones. The terminal, vim, plain text files in a folder, a single make target that takes three seconds to run and tells me, precisely, what it did. I have tried the fast ones — the cloud IDEs, the AI pair-programming that types faster than I can think, the auto-completing editors that anticipate my next move — and I keep coming back to the slow ones. Not out of nostalgia. Because they teach me things.',
      'The thing a slow tool teaches you is what a fast tool hides. When you type a command and wait for it to finish, you feel the cost. You learn that this command does one thing, that it took half a second because it had to read a file from disk, that the file is small but the disk is far. A fast tool collapses all of that into a result, and the result is correct, but you do not learn anything from it. You only learn when the result is wrong, and by then it is too late.',
      'There is also a small pleasure to the slowness itself. The half-second pause before a search result. The two-second compile. The five-second deploy. These are not wastes of time. They are breaths in the middle of a long thought. A fast tool gives you no room to think between actions; a slow tool gives you a beat, and in that beat you sometimes notice that you did not actually want to do the thing you were about to do.',
      'I am not arguing for slow as a virtue. A slow tool that does the wrong thing is still a bad tool. But among tools that do roughly the right thing, the slower one is usually the one that ages better, the one I am still using five years later, the one I have actually learned to think with rather than just operate.',
    ],
    tags: ['tools', 'philosophy'],
    positionOnOrbit: { sphereRadius: 4.3, theta: 3.9269908169872414, phi: 2.4 },
    scale: 0.31,
    spinAxis: [0.3, 0.7, -0.2],
    spinSpeed: 0.04,
    interactive: true,
  },
  {
    id: 7,
    title: 'Writing code that survives a rewrite',
    date: '2025-10-15',
    read: '8 min',
    tag: 'patterns',
    excerpt:
      'On the small structural choices that make a future rewrite cheap instead of catastrophic.',
    body: [
      'Every large system gets rewritten. The only question is whether the rewrite is a quiet afternoon or a six-month nightmare, and most of the answer is decided years before the rewrite starts. The code that survives a rewrite well is not the clever code. It is the boring code, the code with clear contracts, the code whose seams are obvious.',
      'Three patterns seem to matter most. First, naming: a function named compressUserAvatar() is impossible to misread; a function named doIt() is impossible to use safely. The cost of a good name is paid once, in the moment of writing. The benefit compounds forever.',
      'Second, decoupling at the boundary, not in the middle. A module that hides its internals behind a tiny public surface can be replaced without breaking callers; a module whose public surface is half of its file cannot. The discipline is to keep the surface as small as possible, even when the file is large.',
      'Third, contracts that are honest. A function whose signature says it returns a user, but actually returns a user-or-null half the time, is a trap. The trap will not be obvious until a future rewrite changes the implementation and every caller breaks at once. The honest contract — User | null in the signature, error handling at every call site — is the one that survives.',
    ],
    tags: ['code', 'patterns'],
    positionOnOrbit: { sphereRadius: 4.7, theta: 4.71238898038469, phi: 0.9 },
    scale: 0.33,
    spinAxis: [-0.1, 1, 0.2],
    spinSpeed: 0.055,
    interactive: true,
  },
  {
    id: 8,
    title: 'A short defense of boring tech',
    date: '2025-08-04',
    read: '4 min',
    tag: 'philosophy',
    excerpt:
      'Why I keep picking the boring stack, the boring storage, the boring deploy — and why I think you should too.',
    body: [
      'The boring tech is the tech that has been around long enough that the rough edges have been smoothed, the docs have been written, the failure modes have been catalogued. Postgres is boring. SQLite is boring. OpenResty is boring. Cron is boring. None of them will impress anyone in a Twitter thread. All of them will still be here in ten years.',
      'The exciting tech is exciting for a reason — it solves a real problem, usually a problem you do not have. The cost of the exciting tech is not the learning curve. The cost is that nobody on the team has used it in production before, so when it breaks at 2am nobody knows where to look. The cost is that the docs are still being written, by the people who wrote the library. The cost is that the next major version might break your code.',
      'I am not arguing against the exciting tech categorically. Sometimes the problem really does require it, and the right move is to use it. But the default — the thing you reach for when you do not have a specific reason to do otherwise — should be the boring thing. The boring thing has known failure modes. The boring thing has a community. The boring thing will outlive your project.',
      'The hardest part is resisting the pull of the new. Every year there is a new framework that promises to be faster, simpler, more elegant than the last one. Some of them are. Most of them are not. The discipline is to wait a year before adopting anything new, and to adopt it only when the cost of staying on the boring thing exceeds the cost of the migration.',
    ],
    tags: ['tech', 'philosophy'],
    positionOnOrbit: { sphereRadius: 4.1, theta: 5.497787143782138, phi: 1.5 },
    scale: 0.28,
    spinAxis: [0.2, 0.8, 0.4],
    spinSpeed: 0.05,
    interactive: true,
  },
];

// ---------------------------------------------------------------------------
// TIMELINE_PATH — a glowing Catmull-Rom spline of 6 points around the
// planet. Each point sits at a latitude / longitude on a sphere of
// radius ~2.4 (just outside the planet body but inside the satellites).
// The path is a single closed loop. TIMELINE_MILESTONES index into the
// curve via `positionOnPath` (0..1 along the curve's arc length).
// ---------------------------------------------------------------------------

/** Convert (lat, lon) in radians to a Vector3 on a sphere of radius r. */
function latLonToVec3(lat: number, lon: number, r: number): Vector3 {
  return new Vector3(
    r * Math.cos(lat) * Math.cos(lon),
    r * Math.sin(lat),
    r * Math.cos(lat) * Math.sin(lon),
  );
}

/** 6 control points around the equator + a small inclination, closing
 *  the loop. Y values are slight sine bumps so the path looks like a
 *  hand-drawn ribbon rather than a perfect circle. */
const TIMELINE_CONTROL_POINTS: Vector3[] = [
  latLonToVec3(0.05, 0, 2.4),
  latLonToVec3(0.18, Math.PI / 3, 2.4),
  latLonToVec3(-0.1, (2 * Math.PI) / 3, 2.4),
  latLonToVec3(-0.2, Math.PI, 2.4),
  latLonToVec3(0.12, (4 * Math.PI) / 3, 2.4),
  latLonToVec3(0.0, (5 * Math.PI) / 3, 2.4),
];

/** Catmull-Rom closed curve — used for both the tube geometry and for
 *  sampling milestone positions. */
export const TIMELINE_PATH: CatmullRomCurve3 = new CatmullRomCurve3(
  TIMELINE_CONTROL_POINTS,
  true, // closed
  'centripetal',
  0.5,
);

/** Sample point on the curve at a normalized 0..1 arc-length. */
export function timelinePointAt(t: number): Vector3 {
  return TIMELINE_PATH.getPointAt(clamp01(t));
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

// ---------------------------------------------------------------------------
// TIMELINE_MILESTONES — 6 chapters of the journey, distributed evenly
// along the path. positionOnPath 0..1 is along the curve's arc length.
// ---------------------------------------------------------------------------

export interface MilestoneConfig {
  year: string;
  label: string;
  positionOnPath: number; // 0..1
  /** 2-3 sentence personal story for the detail panel. */
  story?: string[];
}

export const TIMELINE_MILESTONES: MilestoneConfig[] = [
  {
    year: '2019',
    label: 'Started shipping small tools',
    positionOnPath: 0.0,
    story: [
      'I shipped my first real tool in 2019 — a tiny CLI that scraped a public website and turned the data into a CSV. It was 80 lines of Python, it broke every time the site changed its markup, and three people used it daily. That was the moment I realized the bar for "useful" was much lower than I thought.',
      'After that I started building one small thing a month. Most of them were useless. A few stuck. The point was not the tools — it was the habit of finishing them.',
    ],
  },
  {
    year: '2021',
    label: 'First product, first team',
    positionOnPath: 0.2,
    story: [
      'In 2021 I joined a small product team as the third engineer. The product was a B2B analytics dashboard, and the codebase was a mix of clean architecture and "we will clean it up next quarter". I learned more in that year than in the previous three combined, mostly about how decisions get made when there are more than one of you in the room.',
      'I also learned that I preferred being a small part of a small team to being a senior engineer at a large one. I left at the end of the year with a notebook full of opinions.',
    ],
  },
  {
    year: '2023',
    label: 'Switched to full-time indie',
    positionOnPath: 0.4,
    story: [
      'I went full-time indie in early 2023. The plan was to ship a SaaS product in six months and to have paying customers by the end of the year. The actual plan was: panic for four months, build a working product in two, and then spend the next six months figuring out how to sell it.',
      'The product was VICOO, which is still my main project. It has never made me rich, but it has been a steady, low-stress source of income — and that, in 2023, felt like the only honest goal.',
    ],
  },
  {
    year: '2024',
    label: 'VICOO public beta',
    positionOnPath: 0.6,
    story: [
      'VICOO went to public beta in spring 2024. The launch was not a single moment — there was no ProductHunt post, no press release. I just turned off the "request access" page one Tuesday and tweeted about it. About 200 people signed up that week; about 60 of them are still using it.',
      'The most interesting thing I learned from the beta was how much the product changes once you have even ten real users. Every assumption I had about which features mattered turned out to be roughly half-right.',
    ],
  },
  {
    year: '2025',
    label: 'Three.js / WebGL deep-dive',
    positionOnPath: 0.8,
    story: [
      'In 2025 I spent most of the year learning Three.js and WebGL in earnest. I started with the official docs, then built a series of small experiments, then tried to recreate a few classic 3D demos from scratch without looking at the source.',
      'The thing I love about 3D on the web is that it is, fundamentally, a compiler problem — you write a tree of nodes, the renderer walks it, and pixels appear. Once that mental model clicked, everything else was just details.',
    ],
  },
  {
    year: '2026',
    label: 'Personal Planet Observatory',
    positionOnPath: 1.0,
    story: [
      'The Personal Planet Observatory is this site. It is the project I built to teach myself how to ship a real-time 3D scene with enough polish to feel like a product, not a demo. Every chapter is a scene, every scene has its own landmark, and the whole thing is one long scroll.',
      'I do not know what the next milestone is. Maybe a VICOO v2, maybe something new. The point of the timeline, in the end, is to make the path visible — not to predict where it goes.',
    ],
  },
  {
    year: '2027',
    label: "What's next",
    positionOnPath: 0.95,
    story: [
      "The next year is the year I want to stop building new projects and start deepening the ones that already exist. VICOO has a long tail of small features that nobody has asked for and that I want to ship anyway. The planet has a second chapter in it that I have not built yet — a closer view, a slower camera, a chapter that reads like a quiet room instead of a tour. The notes archive has grown large enough to deserve its own search.",
      "I do not have a roadmap for any of this in the product sense. I have a vague sense that the work between now and 2028 is mostly about subtraction — fewer features, more polish, slower releases, longer pauses between things — and that is the most exciting kind of work for me. We will see what the path looks like once I start walking it.",
    ],
  },
];

// ---------------------------------------------------------------------------
// CONTACT — the signal tower's info card.
// ---------------------------------------------------------------------------

export interface ContactConfig {
  email: string;
  github: string;
  mastodon: string;
  rss: string;
  copy: string;
}

export const CONTACT: ContactConfig = {
  email: 'hello@yhazrin.xyz',
  github: 'https://github.com/Yhazrin',
  mastodon: 'https://hachyderm.io/@yhazrin',
  rss: '/feed.xml',
  copy: 'Email is the best way to reach me. I usually reply within a week — a thoughtful note always gets one back.',
};

// ---------------------------------------------------------------------------
// HUD panels — content keyed by `panel` name. Consumed by the React HUD
// layer. Plain strings / arrays, no React here.
// ---------------------------------------------------------------------------

export interface IntroPanelContent {
  kicker: string;
  heading: string;
  byline: string;
  meta: string;
}

export interface AboutPanelContent {
  name: string;
  role: string;
  bio: string;
  meta: { location: string; stack: string; since: string };
  tags: string[];
}

export interface SkillsPanelContent {
  categories: { name: string; items: string[] }[];
}

export const HUD_PANELS = {
  intro: {
    kicker: 'Index 01 / 2026',
    heading: 'hello\nyhazrin',
    byline: 'Software engineer. Writes code, ships things, takes notes.',
    meta: 'Est. 2019 · Kuala Lumpur',
  } satisfies IntroPanelContent,
  about: {
    name: 'yhazrin',
    role: 'Software engineer based in Kuala Lumpur.',
    bio:
      'I work on developer tools and interfaces — TypeScript, React, Three.js, and the messy edges where they meet systems. I care about clear writing, fast pages, and software that respects your time. Off-hours I read, run, and over-engineer my dotfiles.',
    meta: { location: 'Kuala Lumpur', stack: 'TypeScript / Three.js', since: '2019' },
    tags: ['AI', 'WebGL', 'Three.js', 'Full-stack', 'Creative Coding'],
  } satisfies AboutPanelContent,
  skills: {
    categories: [
      { name: 'Frontend', items: ['React 18', 'TypeScript', 'Vite', 'Next.js', 'Framer Motion'] },
      {
        name: 'Three.js / WebGL',
        items: ['@react-three/fiber', 'Three.js helpers', 'GLSL basics', 'Procedural geometry'],
      },
      { name: 'Backend', items: ['Node.js', 'TypeScript', 'OpenResty', 'Postgres', 'Redis'] },
      {
        name: 'AI / Agent',
        items: ['Prompt engineering', 'Tool use', 'Multi-agent orchestration'],
      },
      { name: 'Tools', items: ['Git', 'Docker', 'Playwright', 'pnpm', 'Vite'] },
    ],
  } satisfies SkillsPanelContent,
} as const;

// ---------------------------------------------------------------------------
// Convenience exports
// ---------------------------------------------------------------------------

/** Total page height multiplier — App.tsx sets body min-height to this
 *  many viewport heights. 7 scenes, 700vh gives ~100vh per scene. */
export const PAGE_HEIGHT_VH = 700;

/** Number of scenes. */
export const SCENE_COUNT = SCENES.length;
